// ─────────────────────────────────────────────────────────────────────
// Route tree for everything that renders INSIDE a tab.
//
// Rendered through a per-tab <MemoryRouter> by `TabsOutlet`. Shell
// chrome (Authenticated, ThemedLayout, RealtimeProvider,
// etc.) wraps it ONCE at the outer level — do not mount any of that
// here. See docs/IN_APP_TABS.md §8.
// ─────────────────────────────────────────────────────────────────────

import React from "react";
import { Route, Routes, Navigate } from "react-router";
import { NavigateToResource } from "@refinedev/react-router";
import { ErrorComponent } from "@refinedev/antd";

import { RequireRole } from "@components/RequireRole";

// ── Lazy-loaded tab-level pages ─────────────────────────────────────────
const lazy = <K extends string, P>(
  factory: () => Promise<Record<K, React.ComponentType<P>>>,
  name: K,
): React.LazyExoticComponent<React.ComponentType<P>> =>
  React.lazy(() => factory().then((m) => ({ default: m[name] })));

const CompaniesList = lazy(() => import("../entities/companies/CompaniesList"), "CompaniesList");
const CompanyPage = lazy(() => import("../entities/companies/CompanyPage"), "CompanyPage");
const ProductsList = lazy(() => import("../entities/products/ProductsList"), "ProductsList");
const ProductPage = lazy(() => import("../entities/products/ProductPage"), "ProductPage");
const PriceListList = lazy(() => import("../entities/price-lists/PriceListsList"), "PriceListsList");
const PriceListPage = lazy(() => import("../entities/price-lists/PriceListPage"), "PriceListPage");
const InvoicesIssuedList = lazy(() => import("../entities/invoices/InvoicesList"), "InvoicesIssuedList");
const InvoicesReceivedList = lazy(() => import("../entities/invoices/InvoicesList"), "InvoicesReceivedList");
const InvoicePage = lazy(() => import("../entities/invoices/InvoicePage"), "InvoicePage");
const TransactionsV2Page = lazy(
  () => import("../entities/transactions/TransactionsList"),
  "TransactionsList",
);
const TransactionPage = lazy(() => import("../entities/transactions/TransactionPage"), "TransactionPage");
const HomePage = lazy(
  () => import("../pages/home/HomePage"),
  "HomePage",
);
const AdminTaskQueuePage = lazy(
  () => import("../pages/queues/AdminTaskQueuePage"),
  "AdminTaskQueuePage",
);
const LogisticsPipelinePage = lazy(
  () => import("../pages/queues/LogisticsPipelinePage"),
  "LogisticsPipelinePage",
);
const LogisticsTaskQueuePage = lazy(
  () => import("../pages/queues/LogisticsTaskQueuePage"),
  "LogisticsTaskQueuePage",
);
const LogisticsLoadAlertsPage = lazy(
  () => import("../pages/queues/LogisticsLoadAlertsPage"),
  "LogisticsLoadAlertsPage",
);
const SalesTaskQueuePage = lazy(
  () => import("../pages/queues/SalesTaskQueuePage"),
  "SalesTaskQueuePage",
);
const EdprList = lazy(() => import("../entities/edpr/EdprList"), "EdprList");
const EdprPage = lazy(() => import("../entities/edpr/EdprPage"), "EdprPage");
const EdprTaskQueuePage = lazy(() => import("../entities/edpr/queue/EdprTaskQueuePage"), "EdprTaskQueuePage");
const EdprRequestsList = lazy(() => import("../entities/edpr-requests/EdprRequestsList"), "EdprRequestsList");
const EdprRequestPage = lazy(() => import("../entities/edpr-requests/EdprRequestPage"), "EdprRequestPage");
const BankInboxPage = lazy(() => import("../pages/bank/BankInboxPage"), "BankInboxPage");
const BankDashboardPage = lazy(
  () => import("../pages/bank/BankDashboardPage"),
  "BankDashboardPage",
);
const BankStatementsList = lazy(
  () => import("../entities/bank-statements/BankStatementsList"),
  "BankStatementsList",
);
const BankStatementView = lazy(
  () => import("../entities/bank-statements/BankStatementView"),
  "BankStatementView",
);
const BankOperationsList = lazy(
  () => import("../entities/bank-operations/BankOperationsList"),
  "BankOperationsList",
);
const BankOperationsView = lazy(
  () => import("../entities/bank-operations/BankOperationsView"),
  "BankOperationsView",
);
const BankPaymentsList = lazy(
  () => import("../entities/bank-payments/BankPaymentsList"),
  "BankPaymentsList",
);
const PairingPage = lazy(
  () => import("../entities/bank-payments/pairing/PairingPage"),
  "PairingPage",
);
const PayableInvoicesPage = lazy(
  () => import("../entities/payable-invoices/PayableInvoicesList"),
  "PayableInvoicesPage",
);
const BankAccountVerificationList = lazy(
  () => import("../entities/bank-account-verification/BankAccountVerificationList"),
  "BankAccountVerificationList",
);
const AppConfigurationList = lazy(
  () => import("../entities/app-configuration/AppConfigurationList"),
  "AppConfigurationList",
);
const ReportsList = lazy(() => import("../pages/reports/list/ReportsList"), "ReportsList");
const ReportViewer = lazy(() => import("../pages/reports/viewer/ReportViewer"), "ReportViewer");
const ReportEditor = lazy(() => import("../pages/reports/admin/ReportEditor"), "ReportEditor");
const TemplateConfigListPage = lazy(
  () => import("../pages/settings/template-configs/TemplateConfigListPage"),
  "TemplateConfigListPage",
);
const TemplateConfigFormPage = lazy(
  () => import("../pages/settings/template-configs/TemplateConfigFormPage"),
  "TemplateConfigFormPage",
);
const GmailOAuthPage = lazy(() => import("../pages/settings/GmailOAuthPage"), "GmailOAuthPage");
const PipelineJobsList = lazy(() => import("../entities/pipeline/PipelineJobsList"), "PipelineJobsList");
const PipelineAsyncJobsList = lazy(() => import("../entities/pipeline/PipelineAsyncJobsList"), "PipelineAsyncJobsList");
const TransactionRulesPage = lazy(
  () => import("../pages/settings/rules/TransactionRulesPage"),
  "TransactionRulesPage",
);
const TransactionRuleFormPage = lazy(
  () => import("../pages/settings/rules/TransactionRuleFormPage"),
  "TransactionRuleFormPage",
);
const InvoiceApprovalRulesPage = lazy(
  () => import("../pages/settings/rules/InvoiceApprovalRulesPage"),
  "InvoiceApprovalRulesPage",
);
const InvoiceWarningClausesPage = lazy(
  () => import("../pages/settings/rules/InvoiceWarningClausesPage"),
  "InvoiceWarningClausesPage",
);
const EmployeesList = lazy(() => import("../entities/employees/EmployeesList"), "EmployeesList");
const EmployeePage = lazy(() => import("../entities/employees/EmployeePage"), "EmployeePage");
const DeliveryTargetsList = lazy(() => import("../entities/delivery-targets/DeliveryTargetsList"), "DeliveryTargetsList");
const AuditLogPage = lazy(() => import("../pages/audit-log/AuditLogPage"), "AuditLogPage");
const CommissionRulesList = lazy(() => import("../entities/commission/CommissionRulesList"), "CommissionRulesList");
const CommissionReportsPage = lazy(() => import("../entities/commission/reports/CommissionReportsPage"), "CommissionReportsPage");
const CommissionReportsList = lazy(() => import("../entities/commission/reports/CommissionReportsList"), "CommissionReportsList");
const CommissionPayeesList = lazy(() => import("../entities/commission/CommissionPayeesList"), "CommissionPayeesList");
const EdprRatesList = lazy(() => import("../entities/commission/EdprRatesList"), "EdprRatesList");
const CommissionConfigPage = lazy(() => import("../entities/commission/config/CommissionConfigPage"), "CommissionConfigPage");

export function TabbableRoutes() {
  return (
    <Routes>
      <Route path="/companies">
        <Route index element={<CompaniesList />} />
        <Route path=":id" element={<CompanyPage />} />
      </Route>

      <Route path="/products">
        <Route index element={<ProductsList />} />
        <Route path=":id" element={<ProductPage />} />
      </Route>

      <Route path="/invoices" element={<RequireRole gate="invoice_show" />}>
        <Route index element={<Navigate to="issued" replace />} />
        <Route path="issued" element={<InvoicesIssuedList />} />
        <Route path="received" element={<InvoicesReceivedList />} />
        {/* Payment prep lives in the Faktury section; the component keeps its
            own bank_show content gate on top of this block's invoice_show. */}
        <Route path="payable" element={<PayableInvoicesPage />} />
        <Route path="sync-review/:direction" element={<Navigate to="/invoices/received" replace />} />
        <Route path="edit/:id" element={<Navigate to="/invoices/received" replace />} />
        <Route path="sync" element={<Navigate to="/invoices/received" replace />} />
        <Route path=":id" element={<InvoicePage />} />
      </Route>

      <Route path="/transactions">
        <Route index element={<TransactionsV2Page />} />
        <Route path=":id" element={<TransactionPage />} />
      </Route>

      <Route path="/price-lists" element={<RequireRole gate="price_list_show" />}>
        <Route index element={<PriceListList />} />
        <Route path=":id" element={<PriceListPage />} />
      </Route>

      {/* Document pipeline — every logged-in user. */}
      <Route path="/dokumenty" element={<PipelineJobsList />} />
      {/* Old location — redirect kept for deep links / queue links. Outside
          the RequireRole gate so every role reaches the new page. */}
      <Route path="/automatyzacja/jobs" element={<Navigate to="/dokumenty" replace />} />

      <Route path="/automatyzacja" element={<RequireRole gate="automation_show" />}>
        <Route path="async-jobs" element={<PipelineAsyncJobsList />} />
      </Route>

      <Route path="/bank" element={<RequireRole gate="bank_show" />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<BankDashboardPage />} />
        <Route path="inbox" element={<BankInboxPage />} />
        <Route path="operations" element={<BankOperationsList />} />
        <Route path="operations/:id" element={<BankOperationsView />} />
        <Route path="pairing" element={<PairingPage />} />
        <Route path="payments" element={<BankPaymentsList />} />
        {/* Moved under /invoices (Szymon, 2026-08-20); redirect keeps old links
            and the pre-move e2e-ui specs (locked) working. */}
        <Route path="payable" element={<Navigate to="/invoices/payable" replace />} />
        <Route path="account-verification" element={<BankAccountVerificationList />} />
        <Route path="statements" element={<BankStatementsList />} />
        <Route path="statements/:id" element={<BankStatementView />} />
      </Route>

      <Route path="/edpr" element={<RequireRole gate="edpr_show" />}>
        <Route index element={<EdprList />} />
        <Route path="queue" element={<EdprTaskQueuePage />} />
        <Route path=":id" element={<EdprPage />} />
      </Route>

      <Route path="/edpr-requests" element={<RequireRole gate="edpr_show" />}>
        <Route index element={<EdprRequestsList />} />
        <Route path=":id" element={<EdprRequestPage />} />
      </Route>

      <Route path="/settings" element={<RequireRole gate="settings_show" />}>
        <Route path="configuration" element={<AppConfigurationList />} />
        <Route path="drive-setup" element={<Navigate to="/settings/import" replace />} />
        <Route element={<RequireRole gate="settings_system_show" />}>
          <Route path="gmail-oauth" element={<GmailOAuthPage />} />
          <Route path="audit-log" element={<AuditLogPage />} />
        </Route>
        <Route element={<RequireRole gate="employee_show" />}>
          <Route path="employees" element={<EmployeesList />} />
          <Route path="employees/:id" element={<EmployeePage />} />
        </Route>
        <Route path="rules" element={<TransactionRulesPage />} />
        <Route path="rules/create" element={<TransactionRuleFormPage />} />
        <Route path="rules/edit/:id" element={<TransactionRuleFormPage />} />
        <Route path="approval-rules" element={<InvoiceApprovalRulesPage />} />
        <Route path="warning-clauses" element={<InvoiceWarningClausesPage />} />
        <Route element={<RequireRole gate="delivery_target_show" />}>
          <Route path="delivery-targets" element={<DeliveryTargetsList />} />
        </Route>
      </Route>
      <Route path="/settings" element={<RequireRole gate="template_show" />}>
        <Route path="template-configs" element={<TemplateConfigListPage />} />
        <Route path="template-configs/create" element={<TemplateConfigFormPage />} />
        <Route path="template-configs/edit/:id" element={<TemplateConfigFormPage />} />
      </Route>
      <Route path="/settings" element={<RequireRole gate="reports_show" />}>
        <Route path="reports" element={<ReportsList />} />
        <Route path="reports/:id" element={<ReportViewer />} />
        <Route path="reports/admin/new" element={<ReportEditor />} />
        <Route path="reports/admin/:id/edit" element={<ReportEditor />} />
      </Route>
      <Route path="/" element={<HomePage />} />
      <Route path="/admin-tasks" element={<RequireRole gate="automation_show" />}>
        <Route index element={<AdminTaskQueuePage />} />
      </Route>
      <Route path="/logistics-pipeline" element={<RequireRole gate="logistics_dashboard_show" />}>
        <Route index element={<LogisticsPipelinePage />} />
      </Route>
      <Route path="/logistics-tasks" element={<RequireRole gate="logistics_dashboard_show" />}>
        <Route index element={<LogisticsTaskQueuePage />} />
      </Route>
      <Route path="/logistics-load-alerts" element={<RequireRole gate="logistics_dashboard_show" />}>
        <Route index element={<LogisticsLoadAlertsPage />} />
      </Route>
      <Route path="/commission" element={<RequireRole gate="commission_self_report_show" />}>
        <Route path="reports" element={<CommissionReportsPage />} />
        <Route path="reports-list" element={<CommissionReportsList />} />
        <Route path="rules" element={<RequireRole gate="commission_edit" />}>
          <Route index element={<CommissionRulesList />} />
        </Route>
        <Route path="payees" element={<RequireRole gate="commission_edit" />}>
          <Route index element={<CommissionPayeesList />} />
        </Route>
        <Route path="edpr-rates" element={<RequireRole gate="commission_edit" />}>
          <Route index element={<EdprRatesList />} />
        </Route>
        <Route path="config" element={<RequireRole gate="commission_edit" />}>
          <Route index element={<CommissionConfigPage />} />
        </Route>
      </Route>

      <Route path="/sales-tasks" element={<RequireRole gate="sales_dashboard_show" />}>
        <Route index element={<SalesTaskQueuePage />} />
      </Route>
      <Route path="/auth/callback" element={<NavigateToResource />} />
      <Route path="*" element={<ErrorComponent />} />
    </Routes>
  );
}
